import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useQueryParams<T extends Record<string, any>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getFromUrl = () => {
    return Object.fromEntries(
      Object.entries(defaults)
        .filter(([key, _]) => searchParams.has(key))
        .map(([key, _]) => {
          const value = searchParams.getAll(key);
          return [key, Array.isArray(defaults[key]) ? value : value[0]];
        }),
    );
  };

  const clearUndefinedParams = (params: Partial<T>) => {
    return Object.fromEntries(
      Object.entries(params)
        .filter(([key, value]) => value !== undefined),
    );
  };

  const [queryParams, setQueryParams] = useState<T>(() => {
    const params = getFromUrl();
    return clearUndefinedParams({ ...defaults, ...params }) as T;
  });

  useEffect(() => {
    setSearchParams(queryParams);
  }, [queryParams]);


  const updateParams = (params: Partial<T>) => {
    const cleared = clearUndefinedParams({ ...queryParams, ...params }) as T;
    setQueryParams(cleared);
  };

  const setParam = (key: keyof T, value: any) => {
    const formattedValue = Array.isArray(value) ? value : value == 'all' ? undefined : value;
    updateParams({ [key]: formattedValue } as Partial<T>);
  };

  return {
    updateParams,
    queryParams,
    setParam,
  };
}
